import { useEffect, useState } from 'react'
import { Check, Wand2, MoreHorizontal, XCircle, Image as ImageIcon, Maximize2, X } from 'lucide-react'
import type { TemplateConcept, TemplateConceptsState } from '../../types'

/**
 * Concept picker rendered inline with each `TemplateConceptAgent` chat
 * turn (see `type: 'concepts'` + `ref.concept_ids`). The parent chat
 * message provides the ordered `conceptIds` for *this* turn; we resolve
 * them against the live `template_concepts.items` so per-item status
 * (`refined`, `finalized`) always reflects the current picker state —
 * even on old messages after a subsequent refine.
 *
 * Actions map to natural-language messages the parent sends via the
 * normal chat send flow. The BE re-interprets those into
 * `concept_action` requests; the FE stays endpoint-agnostic.
 *
 * `phase === 'finalized' | 'skipped'` locks all buttons — every card
 * still renders but non-chosen ones dim; the finalized one gets a
 * "Selected" badge. This keeps the historical picker readable long
 * after the workflow has moved on to generation.
 */

/** Kinds of actions the user can trigger from the picker. Kept as a
 *  discriminated union so the parent can special-case Refine (which
 *  needs to focus/preseed the composer rather than send immediately)
 *  vs the other three (which are fire-and-forget). */
export type ConceptAction =
  | { kind: 'finalize'; conceptId: string; conceptTitle?: string }
  | { kind: 'refine'; conceptId: string; conceptTitle?: string }
  | { kind: 'more' }
  | { kind: 'skip' }

export interface ConceptPickerProps {
  /** Ordered ids referenced by this specific TemplateConceptAgent turn. */
  conceptIds: string[]
  /** Live `response_state.template_concepts` — resolves ids to items
   *  and drives phase-based locking. */
  state?: TemplateConceptsState | null
  /** Fires when the user clicks any action button. Parent decides how
   *  to translate it into a chat message / API call. */
  onAction: (action: ConceptAction) => void
  /** True while a concept-action request is in flight — disables all
   *  buttons so the user can't fire a second action before the BE
   *  has updated the picker phase. */
  isBusy?: boolean
}

export function ConceptPicker({
  conceptIds,
  state,
  onAction,
  isBusy = false,
}: ConceptPickerProps) {
  /** Full-screen preview of a card thumbnail. Card thumbs are only
   *  ~200px tall in the grid — the BE typically returns a much larger
   *  render, so clicking opens a lightbox that displays the full asset
   *  at natural size (bounded by viewport). `null` = closed. */
  const [lightbox, setLightbox] = useState<{
    src: string
    title?: string
  } | null>(null)

  // ESC closes the lightbox — keyboard-first accessibility. Listener
  // attaches only while the lightbox is open so we don't fight other
  // ESC handlers elsewhere in the app.
  useEffect(() => {
    if (!lightbox) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lightbox])

  // Build an id → item lookup once so each card render is O(1).
  const byId = new Map<string, TemplateConcept>()
  for (const item of state?.items ?? []) {
    if (item?.id) byId.set(item.id, item)
  }

  // Resolve this turn's ids. Missing items are skipped rather than
  // rendering a broken placeholder — the BE may prune old concepts.
  const cards: TemplateConcept[] = conceptIds
    .map(id => byId.get(id))
    .filter((c): c is TemplateConcept => Boolean(c))

  if (cards.length === 0) return null

  const phase = state?.phase
  const isLocked = phase === 'finalized' || phase === 'skipped'
  const selectedId =
    state?.selected_concept_id ??
    cards.find(c => c.status === 'finalized')?.id ??
    null

  const buttonsDisabled = isLocked || isBusy

  return (
    // Fragment wrapper keeps the lightbox / prompt modals OUT of the
    // `space-y-3` container above — that utility applies a top margin
    // to every sibling except the first, which would push the fixed
    // full-screen modals down by 0.75rem visually. Rendering them as
    // top-level siblings of the picker `<div>` sidesteps it entirely.
    <>
    <div className="mt-3 space-y-3">
      {/* ── Card grid ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map(card => {
          const isChosen = selectedId != null && card.id === selectedId
          // When the workflow is locked, non-chosen cards fade back so
          // the selection reads at a glance. Individual `refined`
          // children still show their status pill regardless.
          const dim = isLocked && !isChosen
          return (
            <div
              key={card.id}
              className={`group relative rounded-xl border transition-all overflow-hidden bg-white dark:bg-gray-900 ${
                isChosen
                  ? 'border-primary-500 ring-2 ring-primary-500/30 shadow-md'
                  : 'border-gray-200 dark:border-gray-800 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-sm'
              } ${dim ? 'opacity-50' : ''}`}
            >
              {/* Thumbnail — hidden entirely when the BE hasn't supplied
                  one; the card still reads as a text-only concept. */}
              <div className="relative aspect-[4/3] bg-gray-100 dark:bg-gray-800 overflow-hidden">
                {/* BE uses either `thumbnail` or `thumbnail_url` depending
                    on the generator — accept both and fall through to
                    the placeholder icon if neither is present. */}
                {(() => {
                  const thumb =
                    (typeof card.thumbnail_url === 'string' && card.thumbnail_url) ||
                    (typeof (card as any).thumbnail === 'string' && (card as any).thumbnail) ||
                    ''
                  return thumb ? (
                    // Clicking the thumb opens the lightbox — the card
                    // grid uses `object-cover` at ~4:3 which crops the
                    // BE's larger renders. Wrapping in a button gives
                    // us keyboard focus + a hover overlay for
                    // discoverability without needing a separate
                    // "expand" icon in the card layout.
                    <button
                      type="button"
                      onClick={() => setLightbox({ src: thumb, title: card.title || `Concept ${card.id}` })}
                      className="group/thumb absolute inset-0 w-full h-full block cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
                      aria-label={`Enlarge ${card.title || `concept ${card.id}`}`}
                    >
                      <img
                        src={thumb}
                        alt={card.title || card.id}
                        className="w-full h-full object-cover transition-transform duration-200 group-hover/thumb:scale-[1.03]"
                        loading="lazy"
                        onError={e => {
                          // Hide broken thumbs so the placeholder icon
                          // shows through — network hiccups shouldn't
                          // permanently break the card layout.
                          ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                        }}
                      />
                      {/* Hover affordance — a subtle darkening + icon
                          so the user learns the thumb is clickable
                          without cluttering the card at rest. */}
                      <span className="absolute inset-0 bg-black/0 group-hover/thumb:bg-black/25 transition-colors flex items-center justify-center opacity-0 group-hover/thumb:opacity-100">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/90 dark:bg-gray-900/90 text-gray-800 dark:text-gray-100 text-[10px] font-semibold shadow">
                          <Maximize2 className="h-3 w-3" />
                          View
                        </span>
                      </span>
                    </button>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-600">
                      <ImageIcon className="h-8 w-8" />
                    </div>
                  )
                })()}

                {/* Status badges (top-left) */}
                <div className="absolute top-2 left-2 flex flex-col gap-1">
                  {isChosen && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-primary-600 text-white shadow-sm">
                      <Check className="h-3 w-3" />
                      Selected
                    </span>
                  )}
                  {card.status === 'refined' && !isChosen && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-amber-500 text-white shadow-sm">
                      <Wand2 className="h-3 w-3" />
                      Refined
                    </span>
                  )}
                </div>
              </div>

              {/* Text */}
              <div className="p-3 space-y-2">
                <h4
                  className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2"
                  title={card.title || card.id}
                >
                  {card.title || `Concept ${card.id}`}
                </h4>
                {card.summary && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-3">
                    {card.summary}
                  </p>
                )}

                {/* Per-card actions. Finalize is the primary CTA; Refine
                    is a secondary chip. Both hide once the picker is
                    locked — the card just becomes a static tile. */}
                {!isLocked && (
                  <div className="flex items-center gap-1.5 pt-1">
                    <button
                      type="button"
                      onClick={() =>
                        onAction({ kind: 'finalize', conceptId: card.id, conceptTitle: card.title })
                      }
                      disabled={buttonsDisabled}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md bg-primary-600 hover:bg-primary-700 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Check className="h-3 w-3" />
                      Use this
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        onAction({ kind: 'refine', conceptId: card.id, conceptTitle: card.title })
                      }
                      disabled={buttonsDisabled}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:border-primary-300 dark:hover:border-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-700 dark:hover:text-primary-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Wand2 className="h-3 w-3" />
                      Refine
                    </button>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* ── Grid-level actions ──
          Show more + Skip only make sense while the picker is still
          soliciting a decision. Once finalized/skipped the whole bar
          collapses — the cards above already communicate final state. */}
      {!isLocked && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onAction({ kind: 'more' })}
            disabled={buttonsDisabled}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:border-primary-300 dark:hover:border-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-700 dark:hover:text-primary-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
            Show more
          </button>
          <button
            type="button"
            onClick={() => onAction({ kind: 'skip' })}
            disabled={buttonsDisabled}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <XCircle className="h-3.5 w-3.5" />
            Skip suggestions
          </button>
        </div>
      )}
    </div>

      {/* ── Lightbox ──
          Full-screen preview of the clicked thumbnail. Backdrop click
          + ESC key both close (ESC handled by the effect above). The
          image is bounded to 90vh/90vw so wide creatives don't spill
          off-screen while still displaying at their natural aspect.
          Rendered outside the `space-y-3` wrapper above so the
          Tailwind `space-y-3 > * + *` selector doesn't apply a stray
          `margin-top: 0.75rem` to the fixed backdrop. */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-6"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.title || 'Concept preview'}
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Close preview"
          >
            <X className="h-5 w-5" />
          </button>
          <div
            className="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center gap-3"
            // Prevent inner clicks from bubbling up to the backdrop
            // and closing the modal accidentally when the user is just
            // examining the image.
            onClick={e => e.stopPropagation()}
          >
            <img
              src={lightbox.src}
              alt={lightbox.title || 'Concept preview'}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            {lightbox.title && (
              <div className="text-white text-sm font-semibold text-center max-w-full truncate px-4">
                {lightbox.title}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
